import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import { renderTextPreview } from './retreatAdminUtils';
import './RetreatManage.scss';

export interface UpgradeRow {
  id: number;
  title: string;
  content: string;
  userAccount: string;
  userNickName: string;
  date: string;
  views: number;
}

type EditForm = {
  title: string;
  content: string;
  userAccount: string;
};

const emptyForm: EditForm = {
  title: '',
  content: '',
  userAccount: '',
};

export default function UpgradeManage() {
  const [rows, setRows] = useState<UpgradeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchWord, setSearchWord] = useState('');
  const [editRow, setEditRow] = useState<UpgradeRow | null>(null);
  const [form, setForm] = useState<EditForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post<{ ok: boolean; data: UpgradeRow[] }>(
        `${MainURL}/retreatupgrade/admingetall`
      );
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (error) {
      console.error(error);
      alert('등업신청 목록을 불러오지 못했습니다.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchRows();
  }, [fetchRows]);

  const filteredRows = useMemo(() => {
    const word = searchWord.trim().toLowerCase();
    return rows.filter((row) => {
      if (!word) return true;
      const haystack = [row.title, row.content, row.userNickName, row.userAccount]
        .join(' ')
        .toLowerCase();
      return haystack.includes(word);
    });
  }, [rows, searchWord]);

  const openEdit = (row: UpgradeRow) => {
    setEditRow(row);
    setForm({
      title: row.title || '',
      content: row.content || '',
      userAccount: row.userAccount || '',
    });
  };

  const closeEdit = () => {
    setEditRow(null);
    setForm(emptyForm);
  };

  const saveEdit = async () => {
    if (!editRow) return;
    if (!form.title.trim() || !form.content.trim()) {
      alert('제목과 내용은 필수입니다.');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${MainURL}/retreatupgrade/adminupdate`, {
        id: editRow.id,
        title: form.title.trim(),
        content: form.content.trim(),
        userAccount: form.userAccount.trim(),
      });
      if (res.data) {
        alert('저장되었습니다.');
        closeEdit();
        void fetchRows();
      } else {
        alert('저장에 실패했습니다.');
      }
    } catch (error) {
      console.error(error);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const deleteRow = async (row: UpgradeRow) => {
    if (!window.confirm(`「${row.title}」 등업신청을 삭제하시겠습니까?`)) return;
    setActingId(row.id);
    try {
      const res = await axios.post(`${MainURL}/retreatupgrade/admindelete`, { id: row.id });
      if (res.data) {
        setRows((prev) => prev.filter((item) => item.id !== row.id));
        if (editRow?.id === row.id) closeEdit();
      } else {
        alert('삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error(error);
      alert('삭제 중 오류가 발생했습니다.');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="retreat-admin">
      <div className="retreat-admin__toolbar">
        <input
          className="retreat-admin__search"
          type="text"
          placeholder="제목, 내용, 작성자 검색"
          value={searchWord}
          onChange={(e) => setSearchWord(e.target.value)}
        />
        <button type="button" className="retreat-admin__refresh" onClick={() => void fetchRows()}>
          새로고침
        </button>
        <a className="retreat-admin__link" href="/retreat/upgrade" target="_blank" rel="noreferrer">
          사이트 목록 보기
        </a>
      </div>

      {loading ? (
        <div className="retreat-admin__loading">불러오는 중...</div>
      ) : filteredRows.length === 0 ? (
        <div className="retreat-admin__empty">표시할 등업신청이 없습니다.</div>
      ) : (
        <div className="retreat-admin__table-wrap">
          <table className="retreat-admin__table">
            <thead>
              <tr>
                <th>ID</th>
                <th>제목</th>
                <th>작성자</th>
                <th>계정</th>
                <th>조회</th>
                <th>등록일</th>
                <th>관리</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td title={row.title}>{renderTextPreview(row.title)}</td>
                  <td>{row.userNickName}</td>
                  <td title={row.userAccount}>{renderTextPreview(row.userAccount, 24)}</td>
                  <td>{row.views}</td>
                  <td>{row.date ? String(row.date).slice(0, 10) : '-'}</td>
                  <td>
                    <div className="retreat-admin__actions">
                      <button
                        type="button"
                        className="retreat-admin__btn retreat-admin__btn--ghost"
                        onClick={() => openEdit(row)}
                      >
                        수정
                      </button>
                      <button
                        type="button"
                        className="retreat-admin__btn retreat-admin__btn--danger"
                        disabled={actingId === row.id}
                        onClick={() => void deleteRow(row)}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editRow && (
        <div className="retreat-admin__modal-backdrop" onClick={closeEdit}>
          <div className="retreat-admin__modal" onClick={(e) => e.stopPropagation()}>
            <div className="retreat-admin__modal-head">
              <h3>등업신청 수정 — {editRow.title}</h3>
              <button type="button" className="retreat-admin__modal-close" onClick={closeEdit}>
                ×
              </button>
            </div>
            <div className="retreat-admin__modal-body">
              <div className="retreat-admin__field">
                <label htmlFor="upgrade-title">제목</label>
                <input
                  id="upgrade-title"
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className="retreat-admin__field">
                <label htmlFor="upgrade-content">내용</label>
                <textarea
                  id="upgrade-content"
                  value={form.content}
                  onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
                />
              </div>
              <div className="retreat-admin__field">
                <label>작성자 닉네임</label>
                <input value={editRow.userNickName} readOnly />
              </div>
              <div className="retreat-admin__field">
                <label htmlFor="upgrade-userAccount">userAccount</label>
                <input
                  id="upgrade-userAccount"
                  value={form.userAccount}
                  onChange={(e) => setForm((prev) => ({ ...prev, userAccount: e.target.value }))}
                  placeholder="로그인 계정"
                />
              </div>
            </div>
            <div className="retreat-admin__modal-foot">
              <button
                type="button"
                className="retreat-admin__btn retreat-admin__btn--ghost"
                onClick={closeEdit}
              >
                취소
              </button>
              <button
                type="button"
                className="retreat-admin__btn retreat-admin__btn--primary"
                disabled={saving}
                onClick={() => void saveEdit()}
              >
                {saving ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
